import React, { useEffect, useState } from "react";
import LeftIcon from "../../images/LeftIcon";
import ArrowRightIcon from "../../images/ArrowRightIcon";
import DraftIcon from "../../images/DraftIcon";
import SaveIcon from "../../images/SaveIcon";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import { validateNoSpecialChars } from "../../validation/ValidationInput";
import {
  createTerm,
  getDetailTerm,
  updateTerm,
} from "../../services/term.service";
import PrintIcon from "../../images/PrintIcon";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import TagStatus from "../../components/TagStatus";
import BackIcon from "../../images/BackIcon";
import { useResponsive } from "../../hook/useResponsive";
import { useFormKeyboard } from "../../hook/useFormKeyboard";

interface TermsResponse {
  termCode: string;
  termName: string;
  termDescription: string;
  termDay: number;
}

const TermDetail: React.FC = () => {
  const preLanguage = localStorage.getItem("language");
  const [formValues, setFormValues] = useState<TermsResponse>({
    termCode: "",
    termName: "",
    termDescription: "",
    termDay: 0,
  });
  const { isMobile, getWidth } = useResponsive();
  const [errors, setErrors] = useState<any>({});
  const limits = useFieldLimits(coreFormLimits("term"));
  const [isToggled, setIsToggled] = useState(false);
  const [language, setLanguage] = useState<any>(null);
  const handleToggle = () => {
    setIsToggled(!isToggled);
  };
  const path = window.location.pathname;
  const id: string = path.split("/").pop() || "";
  const kb = useFormKeyboard({ autoFocus: id === "new" && !isMobile });
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  useEffect(() => {
    if (id !== "new") {
      fetchData(id);
    }
    const handleNewToken = () => {
      if (id !== "new") {
        fetchData(id);
      }
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, [path]);

  const fetchData = async (id: string) => {
    webLoadingTrue();
    try {
      const response = await getDetailTerm(id);
      setFormValues(response.data);
      setIsToggled(response.data.isDefault);
    } catch (error) {
      console.error("Error loading terms:", error);
    } finally {
      webLoadingFalse();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: any = validateAgainstLimits({ ...formValues }, limits, language);
    if (!formValues.termName) {
      newErrors.termName = "Term Name is required";
    }
    if (formValues.termDay === 0) {
      newErrors.termDay = "Days must be greater than 0";
    }
    if (!formValues.termDescription) {
      newErrors.termDescription = "Description is required";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    webLoadingTrue();
    const payload: any = {
      ...formValues,
      isDefault: isToggled,
      termDay: Number(formValues.termDay),
    };
    if (id === "new") {
      try {
        const response: any = await createTerm(payload);
        if (response.data.id) {
          webNavigate("/" + preLanguage + "/terms");
          showToast({
            message: "Payment Term Created !",
            type: "success",
            duration: 20000,
          });
          webLoadingFalse();
        }
      } catch (error: any) {
        webLoadingFalse();

        showApiError(error, { language, setFieldErrors: setErrors });
      }
    } else {
      try {
        const response: any = await updateTerm(id, payload);
        if (response.data.id) {
          webNavigate("/" + preLanguage + "/terms");
          showToast({
            message: "Payment Term Updated !",
            type: "success",
            duration: 20000,
          });
          webLoadingFalse();
        }
      } catch (error: any) {
        webLoadingFalse();

        showApiError(error, { language, setFieldErrors: setErrors });
      }
    }
  };

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <div className="flex items-center ">
          <button
            type="button"
            aria-label={language?.["Back"] ?? "Back"}
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => {
              webNavigate("/" + preLanguage + "/terms");
            }}
          >
            <BackIcon />
          </button>
          <p
            style={{
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Back"] ? language?.["Back"] : "Back"}
          </p>
        </div>
      </div>
      <div className="lg:flex items-center justify-between">
        {id === "new" ? (
          <p
            style={{
              margin: "20px 0px",
              fontSize: "20px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Add Terms"]
              ? language?.["Add Terms"]
              : "Add Terms"}
          </p>
        ) : (
          <p
            style={{
              margin: "20px 0px",
              fontSize: "20px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Edit Terms"]
              ? language?.["Edit Terms"]
              : "Edit Terms"}
          </p>
        )}
        <div className="flex items-center" style={{ gap: "16px" }}>
          <TagStatus value={"Draft"} />
          <div
            className="cursor-pointer flex items-center"
            style={{
              padding: "8px 16px",
              gap: "8px",
              border: "1px solid #0F7679",
              borderRadius: "30px",
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{ width: "24px", height: "24px" }}
            >
              <PrintIcon />
            </div>
            <p
              className="text-[14px] leading-[22px] font-inter"
              style={{ color: "#0F7679", fontWeight: "500" }}
            >
              {language && language?.["Print"] ? language?.["Print"] : "Print"}
            </p>
          </div>
        </div>
      </div>
      <form
        onSubmit={handleSubmit}
        className="scroll-container"
        style={{
          height: isMobile ? "calc(100% - 213px)" : "calc(100% - 164px)",
          overflowY: "scroll",
        }}
      >
        <div
          className="rounded-md bg-white flex items-start"
          style={{ marginTop: "20px", flexWrap: "wrap", gap: "24px" }}
        >
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Term name"]
                  ? language?.["Term name"]
                  : "Term name"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#BA0E13", fontWeight: "500" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <input
                type="text"
                name="termName"
                {...limitProps(limits, "termName")}
                value={formValues?.termName}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
              <FieldCounter value={formValues?.termName} max={limits.termName?.maxLength} />
            </div>
            {errors.termName && <p className="error">{errors.termName}</p>}
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Code"] ? language?.["Code"] : "Code"}
              </p>
            </div>
            <div className="w-full">
              <input
                type="text"
                name="termCode"
                {...limitProps(limits, "termCode")}
                value={formValues?.termCode}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
              <FieldCounter value={formValues?.termCode} max={limits.termCode?.maxLength} />
            </div>
            {errors.termCode && <p className="error">{errors.termCode}</p>}
          </div>

          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Days"] ? language?.["Days"] : "Days"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#BA0E13", fontWeight: "500" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <input
                type="number"
                name="termDay"
                min="0"
                value={formValues?.termDay}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
            </div>
            {errors.termDay && <p className="error">{errors.termDay}</p>}
          </div>

          <div style={{ width: "100%" }}>
            <div className="space-y-2">
              <p
                className="text-[14px] leading-[20px] font-inter"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Default"]
                  ? language?.["Default"]
                  : "Default"}
              </p>
              <div
                className={`switch ${isToggled ? "on" : "off"}`}
                onClick={handleToggle}
                role="switch"
                aria-checked={isToggled}
                aria-label={language?.["Default"] ?? "Default"}
                tabIndex={0}
                onKeyDown={(e) => {
                  // Space only, as on a native checkbox; Enter is left to the form.
                  if (e.key !== " " || e.ctrlKey || e.metaKey || e.altKey) return;
                  e.preventDefault();
                  if (!e.repeat) handleToggle();
                }}
              >
                <div
                  className={`shadow_toggle slider ${isToggled ? "move-right" : "move-left"}`}
                ></div>
              </div>
            </div>
          </div>

          <div className="space-y-2" style={{ width: "100%" }}>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Description"]
                  ? language?.["Description"]
                  : "Description"}
              </p>
            </div>
            <div className="w-full">
              <textarea
                value={formValues?.termDescription}
                onKeyDown={validateNoSpecialChars}
                name="termDescription"
                {...limitProps(limits, "termDescription")}
                style={{ height: "88px" }}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
              <FieldCounter value={formValues?.termDescription} max={limits.termDescription?.maxLength} />
            </div>
            {errors.termDescription && (
              <p className="error">{errors.termDescription}</p>
            )}
          </div>
        </div>
      </form>
      <div
        className="w-full flex items-center justify-end"
        style={{ marginTop: "24px", gap: "12px" }}
      >
        <button
          type="button"
          onClick={() => {
            webNavigate("/" + preLanguage + "/terms");
          }}
          style={{
            padding: "10px 24px",
            borderRadius: "30px",
            color: "#222525",
            fontSize: "14px",
            fontWeight: "500",
            border: "1px solid #C9D7D8",
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
        </button>
        <button
          type="button"
          {...kb.primaryProps(handleSubmit)}
          style={{
            padding: "10px 24px",
            borderRadius: "30px",
            color: "#FFFFFF",
            fontSize: "14px",
            fontWeight: "500",
            backgroundColor: "#0F7679",
          }}
          className="font-inter cursor-pointer"
        >
          <p className="text-[14px] leading-[22px] font-inter">
            {language && language?.["Save"] ? language?.["Save"] : "Save"}
          </p>
        </button>
      </div>
    </div>
  );
};

export default TermDetail;
